import React, {Suspense, lazy, useEffect} from "react"
import {ConfigProvider, theme} from "antd"
import ruRU from "antd/locale/ru_RU"
import uzUZ from "antd/locale/uz_UZ"
import {useTranslation} from "react-i18next"
import {BrowserRouter, Navigate, Outlet, Route, Routes} from "react-router-dom"
import {Authenticated, Refine} from "@refinedev/core"
import routerBindings, {
    CatchAllNavigate,
    DocumentTitleHandler,
    NavigateToResource,
    UnsavedChangesNotifier
} from "@refinedev/react-router-v6"
import {notificationProvider} from "@refinedev/antd"
import Loading from "./components/loading/Loading"
import {customTheme} from "./constants/Theme"
import {useSelectedApp} from "./features/app/appSlice"
import {authProvider} from "./refine/providers/authProvider"
import {dataProvider} from "./refine/providers/dataProvider"
import {createResources} from "./refine/resources"
import {ShadcnAdminLayout} from "./refine/layout/ShadcnAdminLayout"
import AccessGuard from "./features/access/AccessGuard"
import AppErrorBoundary from "./components/errors/AppErrorBoundary"
import {getAppModulePath, getAppModuleShowPath} from "./features/access/appRegistry"
import {i18nProvider} from "./refine/providers/i18nProvider"
import {setDayjsLanguage} from "./utils/dayjs"
import {isAdminLanguage} from "./utils/language"

const DashboardPage = lazy(() => import("./pages/dashboard/Dashboard"))
const OrdersPage = lazy(() => import("./pages/orders/Orders"))
const OrderDetailsPage = lazy(() => import("./pages/orders/OrderDetails"))
const OrdersProgressFullscreenPage = lazy(() => import("./pages/orders/OrdersProgressFullscreen"))
const ReviewsPage = lazy(() => import("./pages/reviews/Reviews"))
const MenuPage = lazy(() => import("./pages/menu/Menu"))
const PromotionsPage = lazy(() => import("./pages/Marketing"))
const MarketingBannersPage = lazy(() => import("./pages/marketing/Banners"))
const MarketingStoriesPage = lazy(() => import("./pages/marketing/Stories"))
const MarketingPromotionsPage = lazy(() => import("./pages/marketing/Promotions"))
const MarketingSegmentsPage = lazy(() => import("./pages/marketing/Segments"))
const MarketingBroadcastsPage = lazy(() => import("./pages/marketing/Broadcasts"))
const AddressCheckPage = lazy(() => import("./pages/address-check/AddressCheckPage"))
const DeliveryCenterPage = lazy(() => import("./pages/delivery-center/DeliveryCenterPage"))
const DeliveryHeatmapPage = lazy(() => import("./pages/delivery-heatmap/DeliveryHeatmapPage"))
const ReportsPage = lazy(() => import("./pages/reports/ReportsPage"))
const ClientsPage = lazy(() => import("./pages/Clients"))
const TeamPage = lazy(() => import("./pages/employees/Employees"))
const SettingsPage = lazy(() => import("./pages/settings/Settings"))
const SettingsBranchesPage = lazy(() => import("./pages/settings/SettingBranches"))
const SettingsShippingPage = lazy(() => import("./pages/settings/SettingShipping"))
const SettingsPaymentTypesPage = lazy(() => import("./pages/settings/SettingPaymentTypes"))
const SettingsCouriersPage = lazy(() => import("./pages/settings/SettingsCouriers"))
const SettingsReviewsPage = lazy(() => import("./pages/settings/SettingReviews"))
const ProfilePage = lazy(() => import("./refine/pages/ProfilePage"))
const LoginPage = lazy(() => import("./refine/pages/LoginPage"))
const NotFoundPage = lazy(() => import("./refine/pages/NotFoundPage"))

const renderWithSuspense = (node: React.ReactNode) => (
    <Suspense fallback={<Loading />}>{node}</Suspense>
)

const App: React.FC = () => {
    const app = useSelectedApp()
    const {t, i18n} = useTranslation()
    const language = isAdminLanguage(i18n.resolvedLanguage) ? i18n.resolvedLanguage : "ru"
    const resources = React.useMemo(() => createResources(t), [t])
    const antLocale = language === "uz" ? uzUZ : ruRU
    const antTheme = React.useMemo(() => ({
        ...customTheme(app.theme),
        algorithm: app.theme === "dark" ? theme.darkAlgorithm : theme.defaultAlgorithm
    }), [app.theme])

    useEffect(() => {
        document.documentElement.classList.toggle("dark", app.theme === "dark")
    }, [app.theme])

    useEffect(() => {
        document.documentElement.lang = language
        setDayjsLanguage(language)
    }, [language])

    useEffect(() => {
        ConfigProvider.config({
            holderRender: (children) => (
                <ConfigProvider locale={antLocale} theme={antTheme}>
                    {children}
                </ConfigProvider>
            )
        })

        return () => ConfigProvider.config({holderRender: undefined})
    }, [antLocale, antTheme])

    const documentTitleHandler = ({autoGeneratedTitle}: {autoGeneratedTitle: string}) => {
        if (!autoGeneratedTitle) return "Bordik"
        return autoGeneratedTitle.replace(/Refine/gi, "Bordik")
    }

    return (
        <ConfigProvider
            locale={antLocale}
            theme={antTheme}
        >
            <AppErrorBoundary>
                <BrowserRouter
                    future={{
                        v7_startTransition: true
                    }}
                >
                <Refine
                    routerProvider={routerBindings}
                    authProvider={authProvider}
                    dataProvider={dataProvider}
                    notificationProvider={notificationProvider}
                    i18nProvider={i18nProvider}
                    resources={resources}
                    options={{
                        disableTelemetry: true,
                        syncWithLocation: true,
                        warnWhenUnsavedChanges: true
                    }}
                >
                    <Routes>
                        <Route
                            path="/orders/progress/fullscreen"
                            element={renderWithSuspense((
                                <Authenticated
                                    key="orders-progress-fullscreen"
                                    loading={<Loading />}
                                    fallback={<CatchAllNavigate to="/login" />}
                                >
                                    <AccessGuard>
                                        <OrdersProgressFullscreenPage />
                                    </AccessGuard>
                                </Authenticated>
                            ))}
                        />
                        <Route
                            element={(
                                <Authenticated
                                    key="authenticated-routes"
                                    loading={<Loading />}
                                    fallback={<CatchAllNavigate to="/login" />}
                                >
                                    <ShadcnAdminLayout>
                                        <Outlet />
                                    </ShadcnAdminLayout>
                                </Authenticated>
                            )}
                        >
                            <Route index element={renderWithSuspense(<AccessGuard><DashboardPage /></AccessGuard>)} />
                            <Route path={getAppModulePath("orders")} element={renderWithSuspense(<AccessGuard><OrdersPage /></AccessGuard>)} />
                            <Route path={getAppModulePath("orders-progress")} element={renderWithSuspense(<AccessGuard><OrdersPage /></AccessGuard>)} />
                            <Route path={getAppModuleShowPath("orders")} element={renderWithSuspense(<AccessGuard><OrderDetailsPage /></AccessGuard>)} />
                            <Route path={getAppModulePath("reviews")} element={renderWithSuspense(<AccessGuard><ReviewsPage /></AccessGuard>)} />
                            <Route path={getAppModulePath("menu")} element={renderWithSuspense(<AccessGuard><MenuPage /></AccessGuard>)} />
                            <Route path="/menu/category/:categoryId" element={renderWithSuspense(<AccessGuard><MenuPage /></AccessGuard>)} />
                            <Route path="/menu/combos" element={renderWithSuspense(<AccessGuard><MenuPage /></AccessGuard>)} />
                            <Route path="/menu/combos/:comboId" element={renderWithSuspense(<AccessGuard><MenuPage /></AccessGuard>)} />
                            <Route path={getAppModulePath("promotions")} element={renderWithSuspense(<AccessGuard><PromotionsPage /></AccessGuard>)} />
                            <Route path={getAppModulePath("marketing-banners")} element={renderWithSuspense(<AccessGuard><MarketingBannersPage /></AccessGuard>)} />
                            <Route path={getAppModulePath("marketing-stories")} element={renderWithSuspense(<AccessGuard><MarketingStoriesPage /></AccessGuard>)} />
                            <Route path={getAppModulePath("marketing-promotions")} element={renderWithSuspense(<AccessGuard><MarketingPromotionsPage /></AccessGuard>)} />
                            <Route path={getAppModulePath("marketing-segments")} element={renderWithSuspense(<AccessGuard><MarketingSegmentsPage /></AccessGuard>)} />
                            <Route path={getAppModulePath("marketing-broadcasts")} element={renderWithSuspense(<AccessGuard><MarketingBroadcastsPage /></AccessGuard>)} />
                            <Route path={getAppModulePath("address-check")} element={renderWithSuspense(<AccessGuard><AddressCheckPage /></AccessGuard>)} />
                            <Route path={getAppModulePath("delivery-center")} element={renderWithSuspense(<AccessGuard><DeliveryCenterPage /></AccessGuard>)} />
                            <Route path={getAppModulePath("delivery-heatmap")} element={renderWithSuspense(<AccessGuard><DeliveryHeatmapPage /></AccessGuard>)} />
                            <Route path={getAppModulePath("reports")} element={renderWithSuspense(<AccessGuard><ReportsPage /></AccessGuard>)} />
                            <Route path={getAppModulePath("clients")} element={renderWithSuspense(<AccessGuard><ClientsPage /></AccessGuard>)} />
                            <Route path="/team" element={<Navigate to={getAppModulePath("settings-team")} replace />} />
                            <Route path={getAppModulePath("profile")} element={renderWithSuspense(<AccessGuard><ProfilePage /></AccessGuard>)} />
                            <Route path={getAppModulePath("settings")} element={renderWithSuspense(<AccessGuard><SettingsPage /></AccessGuard>)} />
                            <Route path={getAppModulePath("settings-team")} element={renderWithSuspense(<AccessGuard><TeamPage /></AccessGuard>)} />
                            <Route path={getAppModulePath("settings-branches")} element={renderWithSuspense(<AccessGuard><SettingsBranchesPage /></AccessGuard>)} />
                            <Route path={getAppModulePath("settings-shipping")} element={renderWithSuspense(<AccessGuard><SettingsShippingPage /></AccessGuard>)} />
                            <Route path={getAppModulePath("settings-payment-types")} element={renderWithSuspense(<AccessGuard><SettingsPaymentTypesPage /></AccessGuard>)} />
                            <Route path={getAppModulePath("settings-couriers")} element={renderWithSuspense(<AccessGuard><SettingsCouriersPage /></AccessGuard>)} />
                            <Route path="/settings/banners" element={<Navigate to="/promotions" replace />} />
                            <Route path={getAppModulePath("settings-reviews")} element={renderWithSuspense(<AccessGuard><SettingsReviewsPage /></AccessGuard>)} />
                            <Route path="*" element={renderWithSuspense(<NotFoundPage />)} />
                        </Route>

                        <Route
                            element={(
                                <Authenticated key="public-routes" fallback={<Outlet />}>
                                    <NavigateToResource resource="dashboard" />
                                </Authenticated>
                            )}
                        >
                            <Route path="/login" element={renderWithSuspense(<LoginPage />)} />
                        </Route>
                    </Routes>

                    <DocumentTitleHandler handler={documentTitleHandler} />
                    <UnsavedChangesNotifier />
                </Refine>
                </BrowserRouter>
            </AppErrorBoundary>
        </ConfigProvider>
    )
}

export default App
